/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useContext } from 'react';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import { makeCTAContent } from '../../../../templates/page-builder-helpers';
import { DirectionContext } from '../../../../contexts/DirectionContext';

const simpleGridColumnOverride = css`
  [data-component-name='SimpleGrid'] {
    margin: -12px;
  }

  [class*='SimpleGridstyle__SimpleGridColumn-sc'] {
    width: 100% !important;
    margin: 12px;
  }

  @media (min-width: 639px) {
    [class*='SimpleGridstyle__SimpleGridColumn-sc'] {
      width: unset !important;
    }
  }
`;

const baseOuter = css`
  min-height: 400px;
  max-width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  background-position: 50%;
  background-size: cover;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  @media (max-width: 1024px) {
    min-height: 300px;
  }

  @media (max-width: 639px) {
    background-position: center;
  }

  @media (max-width: 500px) {
    min-height: 345px;
  }
`;

const textOverridesForBanners = css`
  h1 {
    margin: 0 0 12px 0;
  }
  h2,
  h3 {
    margin: 12px 0;
  }
  h4 {
    margin: 8px 0;
  }
  h5,
  h6,
  p {
    margin: 8px 0;
  }
  @media (max-width: 639px) {
    h1 {
      margin: 0 0 8px 0;
    }
    h2,
    h3 {
      margin: 8px 0;
    }
    h4,
    h5,
    h6,
    p {
      margin: 6px 0;
    }
  }
`;

const ieLetsTalkBaseOuter = css`
  min-height: 339px;
  max-width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2em;

  background-position: 50%;
  background-size: cover;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  @media (min-width: 1920px) {
    min-height: 400px;
  }

  @media (max-width: 870px) {
    min-height: 300px;
  }

  @media (max-width: 750px) {
    height: 450px;
    left: 0;
    flex-direction: column;
    justify-content: flex-end;
    margin-top: 0;
  }

  @media (max-width: 450px) {
    height: 375px;
  }
`;

const outerLeft = css`
  justify-content: flex-start;
`;

const outerRight = css`
  justify-content: flex-end;
`;

const outerCenter = css`
  justify-content: center;
`;

const middle = css`
  justify-content: center;
`;

const outerCSS = {
  left: [baseOuter, outerLeft],
  right: [baseOuter, outerRight],
  center: [baseOuter, outerCenter],
  ieLetsTalk: [ieLetsTalkBaseOuter, middle],
};

const baseWrapper = css`
  margin: 0 auto;
  max-width: 1200px;
  width: 100%;
`;

const wrapperCSS = {
  left: [baseWrapper, outerLeft],
  right: [baseWrapper, outerRight],
  center: [baseWrapper, outerCenter],
  ieLetsTalk: [ieLetsTalkBaseOuter, middle],
};

const hideOnMobile = css`
  display: none;
  @media (min-width: 639px) {
    display: block;
  }
`;

const theMobileContentCSS = css`
  padding: 20px;
  background-color: #ffffff;
  @media (min-width: 639px) {
    display: none;
  }

  .richText {
    * {
      color: #000000 !important;
    }
    h1 {
      line-height: 0.9 !important;
      font-size: 27px;

      @media (max-width: 320px) {
        line-height: 1 !important;
      }
    }

    img {
      filter: brightness(0%) contrast(100%);
    }
  }

  .button {
    width: 100% !important;
  }

  .button--tertiary-wrapper {
    button {
      background-color: #333;
      color: #ffffff;
      &:hover {
        background-color: #000;
        color: #ffffff;
        border-color: #000;
      }
    }
  }
`;

const innerLeft = css`
  max-width: 45%;
  min-width: 300px;
  padding-inline-start: 10px;
  color: #ffffff;
  text-align: left;
  @media (max-width: 1024px) {
    max-width: 60%;
    min-width: unset;
    padding-left: 20px; // to line up with VF icon in top menu for tablet w2s.
  }
  @media (max-width: 639px) {
    max-width: 75%;
    min-width: unset;
    padding-left: 10px; // to line up with VF icon in top menu for mobile ws2.
  }
`;

const innerRight = css`
  max-width: 45%;
  min-width: 300px;
  padding: 0 10px;
  color: #ffffff;
  text-align: left;

  @media (min-width: 1930px) {
    margin-right: 10%;
  }
  @media (max-width: 1024px) {
    max-width: 60%;
    min-width: unset;
    padding: 0 70px; // tablet left-right margins for ws2.
    margin-top: 120px;
  }
  @media (max-width: 639px) {
    width: 100%;
    max-width: unset;
    min-width: unset;
    padding: 0 10px; // mobile left-right margins for ws2
    margin-top: 120px;
  }
`;

const innerCenter = css`
  max-width: 60%;
  min-width: 300px;
  padding: 0 10px;
  color: #ffffff;
  text-align: center;

  @media (max-width: 1024px) {
    max-width: unset;
    min-width: unset;
    padding: 0 20px; // tablet left-right margins for ws2.
  }
  @media (max-width: 639px) {
    padding: 0 10px; // mobile left-right margins for ws2
  }
`;

const ieLetsTalk = css`
  height: 190px;
  position: relative;
  left: 15rem;
  color: #ffffff;

  div {
    text-align: left;
  }

  .heading {
    margin: 0;
    @media (max-width: 820px) {
      font-size: 28px;
    }

    @media (max-width: 720px) {
      font-size: 24px;
    }

    @media (max-width: 345px) {
      font-size: 22px;
    }
  }

  .ctaButton {
    &:hover {
      border: 1px solid var(--white);
    }
  }

  .richText {
    margin-bottom: 34px;

    @media (max-width: 750px) {
      text-align: center;
      margin-bottom: 16px;
    }

    @media (max-width: 375px) {
      text-align: center;
      margin-bottom: 16px;
    }
  }

  .ctaButonRow {
    justify-content: flex-start;
    padding: 0;

    @media (max-width: 750px) {
      justify-content: center;
    }

    @media (max-width: 375px) {
      align-items: center;
    }
  }

  @media (max-width: 1024px) {
    height: 168px;
    left: 12.5rem;
  }

  @media (max-width: 820px) {
    left: 11rem;
  }

  @media (max-width: 750px) {
    top: 16px;
    left: 0;
    text-align: center;
  }

  @media (max-width: 390px) {
    left: 0;
    margin-right: 0;
    margin-top: 188px;
    text-align: center;
  }

  @media (max-width: 375px) {
    margin-top: 0;
    top: 30px;
    text-align: center;
  }
`;

const innerCSS = {
  left: innerLeft,
  right: innerRight,
  center: innerCenter,
  ieLetsTalk,
};

// CSS for slanted split banners.
const SplitContainer = styled.div<{ direction: 'rtl' | 'ltr' }>`
  position: relative;
  display: flex;
  flex-direction: column;

  @media (min-width: 1025px) {
    display: grid;
    grid-template-columns: repeat(20, 1fr);
  }

  direction: ltr;
  * {
    direction: ${props => props.direction};
  }

  [class*='CtaButtonRowstyles__SimpleGridWrapper'] {
    [class*='SimpleGridstyle__SimpleGrid-sc'] {
      gap: 24px;
    }
    [spacing='12'] {
      margin: 0;

      width: 100%;
      @media (min-width: 640px) {
        width: unset;
      }

      .button {
        margin: 0 auto;
        width: 100%;
      }
    }
  }
`;

const splitFirstImageCSS = css`
  order: 1;
  grid-column: 8 / 21;
  grid-row: 1;
  position: relative;
  background-position: 40% 30%;

  @media (max-width: 900px) {
    background-position: 45% 50%;
  }

  @media (max-width: 1024px) {
    max-width: initial;
    margin-left: initial;
    background-position: 50% 50%;
  }
`;

const splitSecondImage = css`
  order: 2;
  grid-column: 1 / 12;
  grid-row: 1;
  z-index: 1;
  justify-content: right;
  display: flex;
  flex-direction: row;
  align-items: center;
  background-position: 100% bottom;
  color: #000000;
  text-align: left;
  height: 100%;
  padding-right: 160px;

  @media (max-width: 900px) {
    max-width: 60%;
  }

  @media (max-width: 1024px) {
    justify-content: center;
    overflow: visible;
    position: initial;
    max-width: initial;
    background-position: 0% center;
    padding-right: 0;
  }
`;

const splitSecondImageTextCSS = css`
  max-width: 445px;
  margin: 0 10px;

  @media (max-width: 1024px) {
    margin: 0 20px;
  }

  @media (max-width: 1024px) {
    max-width: initial;
  }

  @media (max-width: 639px) {
    margin: 0 10px;
  }
`;

interface SectionHeroContentProps {
  id: string;
  __typename: 'ContentfulSectionElementRichText';
  text: {
    raw: string;
    references: {
      contentful_id: string;
      gatsbyImageData: IGatsbyImageData;
      description: string;
      file: {
        url: string;
      };
    };
  };
  color?: string;
  textAlign: string;
}
interface ResponsiveImageCollectionProp {
  imageName: string;
  isMainImage: boolean;
  maxWidth: number;
  imageTitle: {
    title: string;
    file: {
      url: string;
    };
    description?: string;
  };
  secondImage?: {
    file: {
      url: string;
    };
  };
}

export interface SectionHeroProps {
  key?: string;
  id?: string;
  __typename?: string;
  name: string;
  textPosition: 'left' | 'center' | 'right' | 'ieLetsTalk';
  content: SectionHeroContentProps[];
  mirrorContent?: boolean;
  responsiveImageCollection?: ResponsiveImageCollectionProp[];
}

type ImageKeys = string | number;

const SectionHero = ({
  textPosition,
  content,
  mirrorContent,
  responsiveImageCollection = [],
}: SectionHeroProps) => {
  const theHeroCSS = outerCSS[textPosition];
  const theContentCSS = innerCSS[textPosition];
  const theContentWrapper = wrapperCSS[textPosition];
  const theContent = makeCTAContent(content);
  const direction = useContext(DirectionContext);

  let bgAltText = '';
  let bannerJSX = null;
  const images = responsiveImageCollection.map(respImage => {
    const container: Record<ImageKeys, any> = {};

    container['source'] = respImage.imageTitle.file.url;
    container['maxWidth'] = respImage.maxWidth;
    container['secondImageSource'] = respImage.secondImage?.file?.url ?? null;

    return container;
  });

  images.sort((a, b) => (b.maxWidth > a.maxWidth && 1) || -1);
  const bgImages = images.reduce((acc, css, idx) => {
    if (idx === 0) {
      return `background-image: url("${css.source}");`;
    }
    return `${acc}
        @media (max-width: ${css.maxWidth}px) {
          & {
            background-image: url("${css.source}");
            }
        };`;
  }, '');

  // We assume the alt text for the responsive image set is the same for all of them, so take the first
  // one that is not "". If none are set, the alt text will be the initial value of "".
  responsiveImageCollection.some(imageData => {
    const isFound = imageData.imageTitle.description !== '';
    if (isFound) {
      bgAltText = imageData.imageTitle.description as string;
    }
    return isFound;
  });

  if (images[0] && images[0].secondImageSource !== null) {
    // Render a slanted split banner.
    // NB - The secondImage is the split background.
    const splitSecondImageBgCSS = css`
      background-image: url(${images[0].secondImageSource});
    `;

    // Option to horizontal flip content
    const styles = {
      horizontalFlipContent: {
        transform: mirrorContent ? 'scaleX(-1)' : 'none',
      },
    };

    bannerJSX = (
      <SplitContainer
        style={styles.horizontalFlipContent}
        direction={direction}
      >
        <div
          role="img"
          aria-label={bgAltText}
          style={styles.horizontalFlipContent}
          css={[theHeroCSS, bgImages, splitFirstImageCSS]}
        ></div>
        <div css={[splitSecondImage, splitSecondImageBgCSS]}>
          <div
            style={styles.horizontalFlipContent}
            css={[splitSecondImageTextCSS, textOverridesForBanners]}
          >
            {theContent}
          </div>
        </div>
      </SplitContainer>
    );
  } else {
    // render a standard banner.
    bannerJSX = (
      <React.Fragment>
        <div css={simpleGridColumnOverride}>
          <div
            role="img"
            aria-label={bgAltText}
            css={[baseOuter, textOverridesForBanners, bgImages]}
          >
            <div css={[theHeroCSS, theContentWrapper]}>
              <div css={[theContentCSS, hideOnMobile]}>{theContent}</div>
            </div>
          </div>
          <div css={theMobileContentCSS}>{theContent}</div>
        </div>
      </React.Fragment>
    );
  }
  return bannerJSX;
};

export default SectionHero;
